import type { BlockUpdateFields, DataOperation } from '@worldathletics/types';

import type { DocumentsState } from '@/context/store/slices/documentDataSlice';

export type PendingOperationEntry = {
  sequence: number;
  operation: DataOperation;
};

export type PendingOperationsSnapshot = {
  throughSequence: number;
  operations: DataOperation[];
};

const RootParentKey = '__root__' as const;

function parentKey(parentId: string | null): string {
  return parentId ?? RootParentKey;
}

export function operationReferencesSourceId(
  op: DataOperation,
  sourceId: string,
): boolean {
  switch (op.op) {
    case 'add':
      return op.block.id === sourceId;
    case 'update':
    case 'remove':
    case 'move':
      return op.id === sourceId;
    case 'reorder':
      return op.childIds.includes(sourceId);
  }
}

function findCancelledAddRemoveIds(operations: DataOperation[]): Set<string> {
  const pendingAdds = new Set<string>();
  const cancelled = new Set<string>();

  for (const op of operations) {
    if (op.op === 'add') {
      pendingAdds.add(op.block.id);
      continue;
    }

    if (op.op === 'remove' && pendingAdds.has(op.id)) {
      cancelled.add(op.id);
      pendingAdds.delete(op.id);
    }
  }

  return cancelled;
}

function shouldDropCancelledOp(
  op: DataOperation,
  cancelledIds: Set<string>,
): boolean {
  if (op.op === 'reorder') {
    return false;
  }

  if (op.op === 'add') {
    return cancelledIds.has(op.block.id);
  }

  return cancelledIds.has(op.id);
}

export function toPendingOperations(
  entries: PendingOperationEntry[],
): DataOperation[] {
  return entries.map(entry => entry.operation);
}

export function snapshotPendingOperations(
  entries: PendingOperationEntry[],
): PendingOperationsSnapshot {
  if (entries.length === 0) {
    return { throughSequence: 0, operations: [] };
  }

  const lastEntry = entries[entries.length - 1];
  return {
    throughSequence: lastEntry.sequence,
    operations: toPendingOperations(entries),
  };
}

export function ackPendingEntries(
  entries: PendingOperationEntry[],
  throughSequence: number,
): PendingOperationEntry[] {
  return entries.filter(entry => entry.sequence > throughSequence);
}

// Acks exactly the given sequences, unlike `ackPendingEntries` which drops
// every entry at or below a threshold — needed for partial-window saves
// (e.g. `persistSourceText`) where entries outside the sent window can have
// lower sequences than entries inside it and must not be silently dropped.
export function ackPendingSequences(
  entries: PendingOperationEntry[],
  sequences: readonly number[],
): PendingOperationEntry[] {
  if (sequences.length === 0) {
    return entries;
  }
  const acked = new Set(sequences);
  return entries.filter(entry => !acked.has(entry.sequence));
}

export function compactPendingOperations(
  operations: DataOperation[],
): DataOperation[] {
  const cancelledIds = findCancelledAddRemoveIds(operations);
  const filtered = operations.filter(
    op => !shouldDropCancelledOp(op, cancelledIds),
  );

  const kept = new Array<boolean>(filtered.length).fill(true);
  const seenRemoveIds = new Set<string>();
  const mergedUpdates = new Map<string, BlockUpdateFields>();
  const firstUpdateIndex = new Map<string, number>();
  const lastReorderIndexByParent = new Map<string, number>();

  for (let index = 0; index < filtered.length; index += 1) {
    const op = filtered[index];

    if (op.op === 'remove') {
      if (seenRemoveIds.has(op.id)) {
        kept[index] = false;
        continue;
      }

      seenRemoveIds.add(op.id);
      continue;
    }

    if (op.op === 'update') {
      const mergedFields = mergedUpdates.get(op.id);
      mergedUpdates.set(
        op.id,
        mergedFields
          ? Object.assign({}, mergedFields, op.fields)
          : { ...op.fields },
      );

      if (!firstUpdateIndex.has(op.id)) {
        firstUpdateIndex.set(op.id, index);
      } else {
        kept[index] = false;
      }
      continue;
    }

    if (op.op === 'reorder') {
      lastReorderIndexByParent.set(parentKey(op.parentId), index);
    }
  }

  for (let index = 0; index < filtered.length; index += 1) {
    if (!kept[index]) {
      continue;
    }

    const op = filtered[index];
    if (op.op !== 'reorder') {
      continue;
    }

    const lastIndex = lastReorderIndexByParent.get(parentKey(op.parentId));
    if (lastIndex !== index) {
      kept[index] = false;
    }
  }

  const compacted: DataOperation[] = [];

  for (let index = 0; index < filtered.length; index += 1) {
    if (!kept[index]) {
      continue;
    }

    const op = filtered[index];
    if (op.op === 'update') {
      const firstIndex = firstUpdateIndex.get(op.id);
      if (firstIndex !== index) {
        continue;
      }

      compacted.push({
        op: 'update',
        id: op.id,
        fields: mergedUpdates.get(op.id) ?? op.fields,
      });
      continue;
    }

    compacted.push(op);
  }

  return compacted;
}

export function assertSourceNodeKey(
  actual: string | null,
  expected: string,
): void {
  if (actual !== expected) {
    throw new Error(
      `Source node key mismatch: expected "${expected}" but editor state belongs to a different document version ("${actual ?? 'null'}").`,
    );
  }
}

export type SourcePersistSnapshot = PendingOperationsSnapshot & {
  // Sequence numbers of every entry in the returned window, in order — the
  // exact set to ack via `ackPendingSequences` once the window's operations
  // are persisted. Do NOT ack via `throughSequence`: entries before the
  // window can carry lower sequence numbers than entries inside it and must
  // not be dropped when they were never sent.
  sequences: number[];
};

export function selectPendingOpsForSourcePersist(
  entries: PendingOperationEntry[],
  sourceId: string,
): SourcePersistSnapshot {
  let firstIndex = -1;
  let lastIndex = -1;

  entries.forEach((entry, index) => {
    if (!operationReferencesSourceId(entry.operation, sourceId)) {
      return;
    }

    if (firstIndex === -1) {
      firstIndex = index;
    }
    lastIndex = index;
  });

  if (firstIndex === -1) {
    return { throughSequence: 0, operations: [], sequences: [] };
  }

  const windowEntries = entries.slice(firstIndex, lastIndex + 1);
  return {
    ...snapshotPendingOperations(windowEntries),
    sequences: windowEntries.map(entry => entry.sequence),
  };
}

// Drops ops that reference a block not yet known to the server and not
// created by a surviving `add` in this same batch ("shippable"), plus
// `reorder`/`move` ops whose parent/target isn't shippable. Reorder is a
// derived ordering hint — a stale id inside it must not fail the whole save,
// so it's filtered down rather than dropped outright unless it becomes
// empty. Runs to a fixed point since dropping one `add` can un-ship its
// descendants (e.g. deleting an unsaved container that has unsaved children).
export function sanitizeStructuralOps(
  state: DocumentsState,
  knownServerIds: ReadonlySet<string>,
  operations: DataOperation[],
): DataOperation[] {
  let current = operations;

  for (;;) {
    const shippable = new Set(knownServerIds);
    for (const op of current) {
      if (op.op === 'add') {
        shippable.add(op.block.id);
      }
    }

    let changed = false;
    const next: DataOperation[] = [];

    for (const op of current) {
      if (op.op === 'add') {
        if (op.parentId !== null && !shippable.has(op.parentId)) {
          changed = true;
          continue;
        }
        next.push(op);
        continue;
      }

      if (op.op === 'reorder') {
        if (op.parentId !== null && !shippable.has(op.parentId)) {
          changed = true;
          continue;
        }
        const seen = new Set<string>();
        const childIds = op.childIds.filter(id => {
          if (!shippable.has(id) || !state.byId[id] || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        });
        if (childIds.length === 0) {
          changed = true;
          continue;
        }
        if (childIds.length !== op.childIds.length) {
          changed = true;
        }
        next.push({ ...op, childIds });
        continue;
      }

      if (op.op === 'update') {
        if (!shippable.has(op.id)) {
          changed = true;
          continue;
        }
        next.push(op);
        continue;
      }

      if (op.op === 'move') {
        if (!shippable.has(op.id)) {
          changed = true;
          continue;
        }
        if (op.newParentId !== null && !shippable.has(op.newParentId)) {
          changed = true;
          continue;
        }
        next.push(op);
        continue;
      }

      // 'remove' — always shippable: its target either existed on the
      // server already or was added earlier in this same session (and thus
      // already in `knownServerIds`); an add+remove pair for the same id
      // within this batch is cancelled by `compactPendingOperations` before
      // this function ever runs.
      next.push(op);
    }

    current = next;
    if (!changed) {
      return current;
    }
  }
}
